import Damage from "../model/crew/Damage";
import Totals from "../model/crew/Totals";
import BigNum from "../model/math/BigNum";
import Statistics from "../model/statistics/Statistics";
import Values from "../model/value/Values";
import { Settings } from "../Settings";

export default class MineDamageLogic {
    /**
     * @param {Values} values
     * @param {Totals} totals
     * @param {Damage} damage
     * @param {Statistics} globalStatistics
     */
    constructor(values, totals, damage, globalStatistics) {
        /**
         * @constant
         * @type {Values}
         */
        this.values = values;

        /**
         * @constant
         * @type {Totals}
         */
        this.totals = totals;

        /**
         * @constant
         * @type {Damage}
         */
        this.damage = damage;

        /**
         * @constant
         * @type {Statistics}
         */
        this.globalStatistics = globalStatistics;

        /**
         * @constant
         * @type {BigNum}
         */
        this.workingMineDamage = new BigNum(0);
        /**
         * @constant
         * @type {BigNum}
         */
        this.prestigeMultiplier = new BigNum(Settings.prestige.multiplierPerPrestige);

        /**
         * @constant
         * @type {BigNum}
         */
        this.rubyMultiplier = new BigNum(Settings.ruby.multiplierPerRuby);

        /**
         * @constant
         * @type {BigNum}
         */
        this.blockMultiplier = new BigNum(Settings.blocks.multiplierPerBlock);

        /**
         * @constant
         * @type {BigNum}
         */
        this.workingPrestige = new BigNum(0);

        /**
         * @constant
         * @type {BigNum}
         */
        this.oneBigNum = new BigNum(1);

        /**
         * @constant
         * @type {BigNum}
         */
        this.perFrameConversionFactor = new BigNum(1 / Settings.time.turnFrames);
    }

    /**
     * This must be invoked whenever a change is made to the mine damage (typically via an upgrade purchase)
     */    
    calculateTotalMineDamagePerTurn() {

        ////////////////////////////////////////
        // Base Damage/Second
        ////////////////////////////////////////

        this.damage.baseDamagePerTurn.copy(this.damage.basePickDamagePerTurn);
        this.damage.baseDamagePerTurn.add(this.values.baseAdditiveBonusPerTurn.getValue());
        this.damage.baseDamagePerTurn.mul(this.damage.goldBaseDamageMultiplier);

        ////////////////////////////////////////
        // Ore Multipliers (from pick upgrades)
        ////////////////////////////////////////

        // Add up the multiplicative bonuses (starting at 1)
        // this.workingMultiBonus.copy(this.oneBigNum);
        // this.workingMultiBonus.add(this.values.baseMultiplicativeBonusValue.getValue());

        this.damage.oreDamageMultiplier.copy(this.oneBigNum);
        this.damage.oreDamageMultiplier.add(this.values.baseMultiplicativeBonusValue.getValue());
        this.damage.oreDamageMultiplier.mul(this.damage.goldOreDamageMultiplier);

        ////////////////////////////////////////
        // Mined Damage Multiplier
        ////////////////////////////////////////

        this.damage.minedDamageMultiplier.copy(this.oneBigNum);
        this.damage.minedDamageMultiplier.add(this.values.minedDamageBonusMultiplier.getValue());
        this.damage.minedDamageMultiplier.mul(this.damage.goldMinedDamageMultiplier);

        ////////////////////////////////////////
        // Prestige
        ////////////////////////////////////////

        // Make sure the prestige multiplier is up to date.
        this.prestigeMultiplier.setValue(Settings.prestige.multiplierPerPrestige + this.values.skillPrestigeBonusMultiplier.getValue());

        // Prestige count bonus.  20% damage bonus per prestige.
        // 1 + (prestigeSum * 0.2);
        this.damage.prestigeDamageMultiplier.copy(this.totals.prestigePointsSum);
        this.damage.prestigeDamageMultiplier.mul(this.prestigeMultiplier);
        this.damage.prestigeDamageMultiplier.add(this.oneBigNum);
        this.damage.prestigeDamageMultiplier.mul(this.damage.goldPrestigeDamageMultiplier);

        ////////////////////////////////////////
        // Ruby
        ////////////////////////////////////////

        if (this.totals.ruby.greaterThanZero()) {

            // Make sure the ruby multiplier is up to date.
            this.rubyMultiplier.setValue(Settings.ruby.multiplierPerRuby + this.values.skillRubyBonusMultiplier.getValue());

            this.damage.rubyDamageMultiplier.copy(this.totals.ruby);
            this.damage.rubyDamageMultiplier.mul(this.rubyMultiplier);
            this.damage.rubyDamageMultiplier.add(this.oneBigNum);
            this.damage.rubyDamageMultiplier.mul(this.damage.goldRubyDamageMultiplier);
        }
        else {
            this.damage.rubyDamageMultiplier.copy(this.oneBigNum);
        }

        ////////////////////////////////////////
        // Blocks
        ////////////////////////////////////////

        const blockBonusMultipleir = this.values.skillBlockBonusMultiplier.getValue();
        if (blockBonusMultipleir > 0 && this.globalStatistics.tilesMined.greaterThanZero()) {
            // Make sure the ruby multiplier is up to date.
            this.blockMultiplier.setValue(Settings.blocks.multiplierPerBlock + blockBonusMultipleir);

            this.damage.blockDamageMultiplier.copy(this.globalStatistics.tilesMined);
            this.damage.blockDamageMultiplier.mul(this.blockMultiplier);
            this.damage.blockDamageMultiplier.add(this.oneBigNum);
            this.damage.blockDamageMultiplier.mul(this.damage.goldBlockDamageMultiplier);
        }
        else {
            this.damage.blockDamageMultiplier.copy(this.oneBigNum);
        }

        ////////////////////////////////////////
        // Depth
        ////////////////////////////////////////

        const depthBonusMultiplier = this.values.skillDepthBonusMultiplier.getValue();
        if (depthBonusMultiplier > 0) {
            this.damage.depthDamageMultiplier.setValue(this.globalStatistics.maxDepth);
            this.damage.depthDamageMultiplier.mulNumber(depthBonusMultiplier);
            this.damage.depthDamageMultiplier.mul(this.damage.goldDepthDamageMultiplier);
        }
        else {
            this.damage.depthDamageMultiplier.copy(this.oneBigNum);
        }

        ////////////////////////////////////////
        // Gold
        ////////////////////////////////////////

        // Gold damage bonus.  x10 multiplier bonus per upgrade.
        // NOTE: Gold damage bonus is calculated in DamageBonusGoldUpgrade.

        ////////////////////////////////////////
        // Multiply it all together
        ////////////////////////////////////////

        this.workingMineDamage.copy(this.damage.baseDamagePerTurn);

        if (this.damage.oreDamageMultiplier.greaterThan(this.oneBigNum)) {
            this.workingMineDamage.multiply(this.damage.oreDamageMultiplier);
        }

        if (this.damage.minedDamageMultiplier.greaterThan(this.oneBigNum)) {
            this.workingMineDamage.multiply(this.damage.minedDamageMultiplier);
        }
        if (this.damage.prestigeDamageMultiplier.greaterThan(this.oneBigNum)) {
            this.workingMineDamage.multiply(this.damage.prestigeDamageMultiplier);
        }
        if (this.damage.depthDamageMultiplier.greaterThan(this.oneBigNum)) {
            this.workingMineDamage.multiply(this.damage.depthDamageMultiplier);
        }
        if (this.damage.goldDamageMultiplier.greaterThan(this.oneBigNum)) {
            this.workingMineDamage.multiply(this.damage.goldDamageMultiplier);
        }
        if (this.damage.rubyDamageMultiplier.greaterThan(this.oneBigNum)) {
            this.workingMineDamage.multiply(this.damage.rubyDamageMultiplier);
        }
        if (this.damage.blockDamageMultiplier.greaterThan(this.oneBigNum)) {
            this.workingMineDamage.multiply(this.damage.blockDamageMultiplier);
        }

        // Update the total mine damage.
        this.damage.totalPickDamagePerTurn.copy(this.workingMineDamage);

        // Update the damage per frame.
        this.damage.totalPickDamagePerFrame.copy(this.damage.totalPickDamagePerTurn);
        this.damage.totalPickDamagePerFrame.mul(this.perFrameConversionFactor);
    }
}